import { useState, useEffect } from 'react';
import { useCounters } from '../contexts/CounterContext';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

/**
 * Custom hook to handle owner URL resolution and access control for counter components
 * Resolves owner URLs to actual counter IDs and enforces access control rules
 * 
 * @param counterId The counter ID or owner URL from the URL parameter
 * @returns Object containing the resolved counter information and access status
 */
export function useOwnerUrl(counterId?: string) {
  const { selectedCounterId, selectCounter, findCounterByOwnerUrl, hasAccessToCounter, loading: countersLoading } = useCounters();
  const { user, isAdmin } = useUser();
  const userEmail = user.email;
  const navigate = useNavigate();
  
  // State for tracking counter resolution and access
  const [isOwnerUrl, setIsOwnerUrl] = useState(false);
  const [actualCounterId, setActualCounterId] = useState<string | undefined>(counterId);
  const [hasAccess, setHasAccess] = useState(true);
  
  // Check if this is an owner URL and enforce access control
  useEffect(() => {
    let mounted = true;
    
    // Skip all processing if counters are still loading
    if (countersLoading) return;
    
    const checkAccess = () => {
      if (!counterId || !mounted) return;
      
      // Try to find a counter with this owner URL
      const counter = findCounterByOwnerUrl(counterId);
      
      if (counter) {
        // Found counter by owner URL
        setActualCounterId(counter._id);
        
        // Set isOwnerUrl to true ONLY if user is the owner or an admin 
        const isOwner = counter.ownerEmail === userEmail;
        setIsOwnerUrl(isOwner || isAdmin);
        
        // Always check access permissions 
        const accessResult = hasAccessToCounter(counter, isOwner || isAdmin);
        setHasAccess(accessResult);
        
        // Redirect if access is denied
        if (!accessResult) {
          setTimeout(() => {
            if (mounted) {
              navigate('/counter-not-found');
            }
          }, 0);
        }
      } else {
        // Not an owner URL, treat as regular counter ID
        setIsOwnerUrl(false);
        setActualCounterId(counterId);
        setHasAccess(true);
      }
    };
    
    checkAccess();
    
    return () => {
      mounted = false;
    };
  }, [counterId, findCounterByOwnerUrl, hasAccessToCounter, navigate, userEmail, isAdmin, countersLoading]);
  
  // Select the counter in context when actualCounterId changes
  useEffect(() => {
    if (actualCounterId && actualCounterId !== selectedCounterId) {
      selectCounter(actualCounterId);
    }
  }, [actualCounterId, selectedCounterId, selectCounter]);
  
  return {
    isOwnerUrl,
    actualCounterId,
    counterIdentifier: isOwnerUrl ? counterId : actualCounterId,
    hasAccess,
    loading: countersLoading
  };
} 